import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  formatTimecode,
  normaliseRef,
  type ShowcasePublicVideo,
} from "@shared/showcase";
import { fetchVideo, NotFoundError, sendBeacon, startSession } from "./api";
import { Player, type PlayerHandle } from "./Player";
import { WatchTracker } from "./tracking";
import { NendaSymbol } from "./NendaSymbol";
import { Transcript } from "./Transcript";

const BEACON_EVERY_MS = 10_000;

/**
 * Progress reporting for one page view.
 *
 * The session is opened on FIRST PLAY, not on page load: link unfurlers,
 * mail scanners and a prospect who opened the tab and never pressed play are
 * not views. sessionStorage keeps a reload in the same tab one session.
 */
function useTracking(video: ShowcasePublicVideo | null) {
  const tracker = useRef<WatchTracker | null>(null);
  const session = useRef<Promise<string | null> | null>(null);

  const flush = useCallback(async () => {
    const t = tracker.current;
    if (!t || !session.current || !t.hasNews) return;
    const id = await session.current;
    if (id) sendBeacon(t.beacon(id));
  }, []);

  const begin = useCallback(() => {
    if (!video || session.current) return;
    tracker.current = new WatchTracker(video.durationSeconds);
    const key = `nenda-video:${video.slug}`;
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(key);
    } catch {
      // storage blocked: one session per page view instead
    }
    const ref = normaliseRef(new URLSearchParams(location.search).get("ref"));
    session.current = stored
      ? Promise.resolve(stored)
      : startSession(video.slug, ref).then((id) => {
          try {
            if (id) sessionStorage.setItem(key, id);
          } catch {
            // ignore
          }
          return id;
        });
  }, [video]);

  useEffect(() => {
    const timer = window.setInterval(() => void flush(), BEACON_EVERY_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") void flush();
    };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [flush]);

  return useMemo(
    () => ({
      begin,
      tick: (t: number, playing: boolean) => {
        if (playing) tracker.current?.tick(t);
      },
      breakPass: () => tracker.current?.breakPass(),
      flush,
    }),
    [begin, flush]
  );
}

export function useVideo(slug: string) {
  const [video, setVideo] = useState<ShowcasePublicVideo | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">(
    "loading"
  );
  useEffect(() => {
    let live = true;
    fetchVideo(slug)
      .then((v) => {
        if (!live) return;
        setVideo(v);
        setState("ready");
        document.title = `${v.title} · Nenda`;
      })
      .catch(
        (e) =>
          live && setState(e instanceof NotFoundError ? "missing" : "error")
      );
    return () => {
      live = false;
    };
  }, [slug]);
  return { video, state };
}

export function Wordmark() {
  return (
    <a
      className="wordmark"
      href="https://nenda.com"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Nenda"
    >
      <NendaSymbol className="wordmark__symbol" />
    </a>
  );
}

export function Unavailable({ state }: { state: "missing" | "error" }) {
  return (
    <div className="unavailable">
      <Wordmark />
      <h1>
        {state === "missing"
          ? "This video isn't available"
          : "Something went wrong"}
      </h1>
      <p>
        {state === "missing"
          ? "The link may have expired or been switched off. Ask the person who sent it for a fresh one."
          : "Please reload the page in a moment."}
      </p>
    </div>
  );
}

function EndCard({
  video,
  onReplay,
}: {
  video: ShowcasePublicVideo;
  onReplay: () => void;
}) {
  return (
    <div className="endcard">
      <p className="endcard__eyebrow">Thanks for watching</p>
      <h2 className="endcard__title">{video.title}</h2>
      <div className="endcard__actions">
        {video.cta && (
          <a
            className="cta"
            href={video.cta.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {video.cta.label}
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M13 5l7 7-7 7M20 12H4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        )}
        <button className="ghost" onClick={onReplay}>
          Watch again
        </button>
      </div>
    </div>
  );
}

export default function WatchPage({
  slug,
  embed,
}: {
  slug: string;
  embed: boolean;
}) {
  const { video, state } = useVideo(slug);
  const player = useRef<PlayerHandle>(null);
  const tracking = useTracking(video);
  const [time, setTime] = useState(0);
  const [captionLang, setCaptionLang] = useState<string | null>(null);
  const seek = useCallback((t: number) => player.current?.seek(t), []);

  const onTick = useCallback(
    (t: number, playing: boolean) => {
      setTime(t);
      tracking.tick(t, playing);
    },
    [tracking]
  );
  const onEnded = useCallback(() => {
    tracking.breakPass();
    void tracking.flush();
  }, [tracking]);

  if (state === "loading")
    return <div className="loading" aria-label="Loading" />;
  if (state !== "ready" || !video)
    return <Unavailable state={state === "missing" ? "missing" : "error"} />;

  const accent = {
    ["--accent" as string]: video.accentColor,
  } as React.CSSProperties;
  const endCard = (
    <EndCard
      video={video}
      onReplay={() => {
        tracking.breakPass();
        player.current?.seek(0);
      }}
    />
  );

  const playerEl = (
    <Player
      ref={player}
      video={video}
      compact={embed}
      onTick={onTick}
      onFirstPlay={tracking.begin}
      onSeeked={tracking.breakPass}
      onEnded={onEnded}
      endCard={endCard}
      onCaptionLang={setCaptionLang}
    />
  );

  if (embed) {
    return (
      <div className="embed" style={accent}>
        {playerEl}
      </div>
    );
  }

  const activeIndex = video.chapters.reduce(
    (hit, c, i) => (c.startSeconds <= time ? i : hit),
    -1
  );

  return (
    <div className="page" style={accent}>
      {video.posterUrl && (
        <div className="backdrop" aria-hidden="true">
          <img src={video.posterUrl} alt="" />
        </div>
      )}
      <div className="glow" aria-hidden="true" />

      <header className="top">
        <Wordmark />
        <span className="pill">
          <span className="pill__dot" />
          Shared privately
        </span>
      </header>

      <main className="layout">
        <section className="hero">
          <h1 className="hero__title">{video.title}</h1>
          {video.pitch && <p className="hero__pitch">{video.pitch}</p>}
        </section>

        <div className="stage">{playerEl}</div>

        <aside className="rail">
          {video.chapters.length > 0 && (
            <nav className="card chapters" aria-label="Chapters">
              <h2 className="card__title">Chapters</h2>
              <ol>
                {video.chapters.map((c, i) => (
                  <li key={c.startSeconds}>
                    <button
                      className={`chapter${i === activeIndex ? " chapter--active" : ""}`}
                      onClick={() => player.current?.seek(c.startSeconds)}
                    >
                      <span className="chapter__time">
                        {formatTimecode(c.startSeconds)}
                      </span>
                      <span className="chapter__title">{c.title}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {video.captions.length > 0 && (
            <Transcript
              captions={video.captions}
              lang={captionLang}
              time={time}
              onSeek={seek}
            />
          )}

          {(video.presenter || video.cta) && (
            <div className="card presenter">
              {video.presenter && (
                <div className="presenter__who">
                  <span className="avatar" aria-hidden="true">
                    {video.presenter.name
                      .split(/\s+/)
                      .map((p) => p[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </span>
                  <div>
                    <div className="presenter__name">
                      {video.presenter.name}
                    </div>
                    {video.presenter.role && (
                      <div className="presenter__role">
                        {video.presenter.role}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {video.cta && (
                <a
                  className="cta cta--block"
                  href={video.cta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {video.cta.label}
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      d="M13 5l7 7-7 7M20 12H4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}

          {video.description && (
            <div className="card description">
              <h2 className="card__title">About</h2>
              {video.description.split(/\n{2,}/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}
        </aside>
      </main>

      <footer className="foot">
        <span>© Nenda</span>
        <span className="foot__privacy">
          No cookies. We count which parts of this video are watched, and
          roughly where from — never who you are.
        </span>
      </footer>
    </div>
  );
}
